import { __assign } from "tslib";
import { mergeStyleSets, HighContrastSelector, ScreenWidthMaxSmall, getScreenSelector, getFocusStyle, IconFontSizes, getHighContrastNoAdjustStyle, } from '@fluentui/react/lib/Styling';
import { MessageBarType } from '@fluentui/react/lib/MessageBar';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';
import { getErrorSemanticColors, getStandardControlSemanticColorsWithTheme, commonHighContrastHoverFocusStyles, commonHighContrastCanvasStyles, commonHighContrastCanvasTextStyles, } from '@ms/acui-common/lib/styling';
import { AppThemeManager, isThemingEnabled } from '@ms/acui-theme';
export var getAppMessageBarClassNames = memoizeFunction(function () {
    return mergeStyleSets({
        messageTitle: {
            fontWeight: 'bold',
            paddingRight: 5,
        },
    });
});
// Returns the background color of the MessageBar root element based on the type of MessageBar.
var getRootBackground = function (messageBarType, palette, semanticColors, errorColors) {
    var themingEnabled = isThemingEnabled();
    switch (messageBarType) {
        case MessageBarType.error:
        case MessageBarType.blocked:
            return themingEnabled ? errorColors.errorBackgroundColor : semanticColors.errorBackground;
        case MessageBarType.severeWarning:
            return themingEnabled ? errorColors.severeBackgroundColor : semanticColors.blockingBackground;
        case MessageBarType.success:
            return themingEnabled ? errorColors.successBackgroundColor : semanticColors.successBackground;
        case MessageBarType.warning:
            return themingEnabled ? errorColors.warningBackgroundColor : semanticColors.warningBackground;
    }
    return palette.neutralLighter;
};
// Returns the icon color based on the type of MessageBar.
var getIconColor = function (messageBarType, theme, semanticColors, errorColors) {
    var themingEnabled = isThemingEnabled();
    switch (messageBarType) {
        case MessageBarType.error:
        case MessageBarType.blocked:
            return themingEnabled ? errorColors.errorIconColor : semanticColors.errorText;
        case MessageBarType.severeWarning:
            return themingEnabled ? errorColors.severeIconColor : semanticColors.errorText;
        case MessageBarType.success:
            return themingEnabled ? errorColors.successIconColor : theme.palette.green;
        case MessageBarType.warning:
            return themingEnabled
                ? getStandardControlSemanticColorsWithTheme(theme).warningIconColor
                : semanticColors.warningText;
    }
    return theme.palette.neutralSecondary;
};
export var getMessageBarButtonStyles = function () {
    return {
        rootHovered: commonHighContrastHoverFocusStyles,
        rootFocused: commonHighContrastHoverFocusStyles,
    };
};
export var getStyles = function (props) {
    var _a, _b, _c, _d;
    var theme = props.theme, className = props.className, messageBarType = props.messageBarType, onDismiss = props.onDismiss, truncated = props.truncated, isMultiline = props.isMultiline, expandSingleLine = props.expandSingleLine;
    var semanticColors = theme.semanticColors, palette = theme.palette, fonts = theme.fonts;
    var errorColors = getErrorSemanticColors(AppThemeManager.getInstance().getThemeMode());
    var SmallScreenSelector = getScreenSelector(0, ScreenWidthMaxSmall);
    var dismissalAndExpandIconStyle = {
        fontSize: IconFontSizes.xSmall,
        height: 16,
        lineHeight: 16,
        width: 16,
        color: palette.neutralPrimary,
    };
    var dismissalAndExpandStyle = [
        getFocusStyle(theme, {
            inset: 1,
            highContrastStyle: {
                outlineOffset: '-4px',
                outlineColor: 'HighlightText',
            },
            borderColor: 'transparent',
        }),
        {
            flexShrink: 0,
            width: 32,
            height: 32,
            padding: '8px 12px',
            selectors: {
                '& .ms-Button-icon': dismissalAndExpandIconStyle,
                '&:active, &:focus, &:hover': [
                    {
                        backgroundColor: 'transparent',
                    },
                    commonHighContrastHoverFocusStyles,
                ],
            },
        },
    ];
    /** Task 4896327: Deprecate Window/WindowText Styling
     * We need this and the sub styling to support HC styling in
     * both IE and Chromium. Once we no longer support IE,
     * we can clean this up */
    var highContrastCanvasSubStyles = {
        selectors: (_a = {},
            _a[HighContrastSelector] = {
                border: '1px solid CanvasText',
            },
            _a),
    };
    return {
        root: [
            theme.fonts.medium,
            {
                background: getRootBackground(messageBarType, palette, semanticColors, errorColors),
                color: palette.neutralPrimary,
                minHeight: 32,
                width: '100%',
                display: 'flex',
                wordBreak: 'break-word',
                selectors: (_b = {
                        '& .ms-Link': __assign({ color: palette.themeDark }, fonts.small)
                    },
                    _b[HighContrastSelector] = __assign(__assign({}, highContrastCanvasSubStyles), { border: '1px solid WindowText' }),
                    _b),
            },
            isMultiline && {
                flexDirection: 'column',
            },
            className,
            commonHighContrastCanvasStyles,
        ],
        content: [
            {
                display: 'flex',
                width: '100%',
                lineHeight: 'normal',
            },
        ],
        iconContainer: [
            {
                position: 'relative',
                fontSize: IconFontSizes.medium,
                width: 16,
                height: 16,
                display: 'flex',
                flexShrink: 0,
                margin: '8px 0 8px 12px',
            },
        ],
        icon: [
            {
                width: '100%',
                height: '100%',
                color: getIconColor(messageBarType, theme, semanticColors, errorColors),
            },
            commonHighContrastCanvasTextStyles,
        ],
        text: [
            __assign(__assign({ minWidth: 0, display: 'flex', flexGrow: 1, margin: 8 }, fonts.small), { selectors: (_c = {},
                    _c[HighContrastSelector] = __assign({}, getHighContrastNoAdjustStyle()),
                    _c) }),
            !onDismiss && {
                marginRight: 12,
            },
        ],
        innerText: [
            {
                lineHeight: 16,
                selectors: {
                    '& span a': {
                        paddingLeft: 4,
                    },
                },
            },
            truncated && {
                overflow: 'visible',
                whiteSpace: 'pre-wrap',
            },
            !isMultiline && {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            !isMultiline &&
                !truncated && {
                selectors: (_d = {},
                    _d[SmallScreenSelector] = {
                        overflow: 'visible',
                        whiteSpace: 'pre-wrap',
                    },
                    _d),
            },
            expandSingleLine && {
                overflow: 'visible',
                whiteSpace: 'pre-wrap',
            },
        ],
        dismissal: [dismissalAndExpandStyle],
        expand: [dismissalAndExpandStyle],
        actions: [
            {
                display: 'flex',
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: 'auto',
                flexDirection: 'row-reverse',
                alignItems: 'center',
                margin: '0 12px 0 8px',
                selectors: {
                    '& button:nth-child(n+2)': {
                        marginLeft: 8,
                    },
                },
            },
            isMultiline && {
                marginBottom: 8,
            },
        ],
    };
};
//# sourceMappingURL=AppMessageBar.styles.js.map